import React, { useState, useEffect } from 'react'; // Added useEffect
import { useNavigate, useLocation } from 'react-router-dom'; // Added useLocation for better UX
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Stack,
  Button,
  IconButton,
  Drawer,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'; // Added Icon
import logo from '../assets/bouldou logo black.png';
import { useAuth } from '../firebase/authState';
import { doc, getDoc } from 'firebase/firestore'; // Added for role checking
import { db } from '../firebase'; // Ensure your firebase config is imported

// Shared Constants
const primaryColor = '#B200BB';
const lightGrey = '#F4F7FB';

const AuthButtonBase = {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  padding: '8px 18px',
  borderRadius: '25px',
  textTransform: 'none',
  boxShadow: 'none',
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState('user'); // Local state for role

  // Fetch User Role from Firestore
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role || 'user');
        }
      } else {
        setUserRole('user');
      }
    };
    fetchUserRole();
  }, [user]);

  const scrollToSection = (sectionId) => {
    // If we are not on the landing page, navigate home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar position="fixed" elevation={1} sx={{ bgcolor: '#FFF', borderBottom: `1px solid ${lightGrey}`, zIndex: 1201 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 1 }}>
            {/* Logo */}
            <Box 
              component="img" 
              src={logo} 
              alt="Bouldou logo" 
              sx={{ height: { xs: 36, md: 44 }, cursor: 'pointer' }} 
              onClick={() => navigate('/')}
            />

            {/* Desktop Nav Links */}
            <Stack
              direction="row"
              spacing={{ xs: 2, md: 4 }}
              sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
            >
              <Button sx={navButtonStyle} onClick={() => scrollToSection('objective')}>Our Mission</Button>
              <Button sx={navButtonStyle} onClick={() => scrollToSection('how-to-start')}>How to Start</Button>
              {/* <Button sx={navButtonStyle} onClick={() => scrollToSection('pricing')}>Pricing</Button> */}
              <Button sx={navButtonStyle} onClick={() => scrollToSection('contact')}>Contact Us</Button>
              
              {/* ADMIN TAB - Desktop */}
              {userRole === 'admin' && (
                <Button 
                  startIcon={<AdminPanelSettingsIcon />}
                  sx={{ ...navButtonStyle, color: primaryColor, fontWeight: 700 }} 
                  onClick={() => navigate('/admin')}
                >
                  Admin Portal
                </Button>
              )}
            </Stack>

            {/* Desktop Auth Buttons */}
            {!authLoading && (
              <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {user ? (
                  <>
                    <Button variant="contained" sx={signUpButtonStyle} onClick={() => navigate('/home')}>Account</Button>
                    <Button variant="outlined" sx={loginButtonStyle} onClick={logout}>Log Out</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outlined" sx={loginButtonStyle} onClick={() => navigate('/signin')}>Log In</Button>
                    <Button variant="contained" sx={signUpButtonStyle} onClick={() => navigate('/signup')}>Sign Up</Button>
                  </>
                )}
              </Stack>
            )}

            {/* Mobile Hamburger */}
            <IconButton
              size="large"
              edge="end"
              sx={{ display: { xs: 'flex', md: 'none' }, color: primaryColor }}
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ width: 280, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={700} color={primaryColor}>Menu</Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <List>
            {/* ADMIN LINK - Mobile */}
            {userRole === 'admin' && (
              <>
                <ListItem button onClick={() => navigate('/admin')} sx={{ bgcolor: `${primaryColor}10`, borderRadius: '10px', mb: 1 }}>
                  <AdminPanelSettingsIcon sx={{ color: primaryColor, mr: 2 }} />
                  <ListItemText primary="Admin Portal" sx={{ color: primaryColor, '& span': { fontWeight: 700 } }} />
                </ListItem>
                <Divider sx={{ my: 1 }} />
              </>
            )}

            <ListItem button onClick={() => scrollToSection('objective')}><ListItemText primary="Our Mission" /></ListItem>
            <ListItem button onClick={() => scrollToSection('how-to-start')}><ListItemText primary="How to Start" /></ListItem>
            {/* <ListItem button onClick={() => scrollToSection('pricing')}><ListItemText primary="Pricing" /></ListItem> */}
            <ListItem button onClick={() => scrollToSection('contact')}><ListItemText primary="Contact Us" /></ListItem>
            
            <Divider sx={{ my: 2 }} />
            
            {user ? (
              <>
                <ListItem button onClick={() => navigate('/home')}><ListItemText primary="My Account" /></ListItem>
                <ListItem button onClick={logout}><ListItemText primary="Log Out" sx={{ color: 'error.main' }} /></ListItem>
              </>
            ) : (
              <>
                <ListItem button onClick={() => navigate('/signin')}><ListItemText primary="Log In" /></ListItem>
                <ListItem button onClick={() => navigate('/signup')}><ListItemText primary="Sign Up" /></ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

// Internal Styles
const navButtonStyle = { color: 'gray', fontWeight: 500, textTransform: 'none', '&:hover': { color: primaryColor } };
const signUpButtonStyle = { ...AuthButtonBase, backgroundColor: primaryColor, color: '#FFF', '&:hover': { backgroundColor: '#9A009A' } };
const loginButtonStyle = { ...AuthButtonBase, borderColor: primaryColor, color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}10` } };

export default Navbar;